import React, { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Sparkles } from "lucide-react";

/**
 * QR code panel shown on the Profile page (and re-usable elsewhere).
 *
 * Encodes the public site URL so a desktop visitor can scan with their
 * phone camera and land directly on CampCompass — where they can then
 * tap "Save to my phone" to install. Removes the friction of typing the
 * URL on a tiny phone keyboard.
 *
 * The URL prefers REACT_APP_PUBLIC_URL so once the custom domain is live
 * (camp-compass.uk) the QR code points there automatically. Falls back to
 * window.location.origin otherwise.
 */
export default function InstallQRCard() {
  const url = useMemo(() => {
    const fromEnv = (process.env.REACT_APP_PUBLIC_URL || "").trim();
    if (fromEnv) return fromEnv.replace(/\/$/, "");
    if (typeof window !== "undefined") return window.location.origin;
    return "https://camp-compass.uk";
  }, []);

  // Pretty label — strip the protocol so the URL under the QR reads cleanly.
  const prettyUrl = url.replace(/^https?:\/\//, "");

  return (
    <section
      className="border-2 hairline-strong bg-white p-6 mb-8"
      data-testid="profile-install-qr"
    >
      <header className="mb-4">
        <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Get the app on your phone
        </div>
        <h2 className="font-display text-xl mt-1">Scan with your phone camera</h2>
        <p className="text-sm text-[#555] leading-relaxed mt-1.5 max-w-xl">
          Point your phone camera at the square below. Tap the link that pops up,
          then tap <strong className="text-[#111]">"Save to my phone"</strong> on
          the page that opens. Two taps and CampCompass lives on your home screen
          — ready next time you're planning a trip.
        </p>
      </header>

      <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-center">
        <div className="inline-flex items-center justify-center p-4 bg-white border-2 border-[#111] shadow-[0_4px_0_0_#ff4500]">
          <QRCodeSVG
            value={url}
            size={168}
            level="M"
            bgColor="#ffffff"
            fgColor="#111111"
            data-testid="install-qr-svg"
            aria-label={`QR code linking to ${prettyUrl}`}
          />
        </div>

        <div className="space-y-3">
          <ol className="space-y-2 text-sm text-[#444]">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#ff4500] text-white inline-flex items-center justify-center mono text-xs font-bold">1</span>
              <span className="leading-relaxed">Open your phone&apos;s camera (no app needed)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#ff4500] text-white inline-flex items-center justify-center mono text-xs font-bold">2</span>
              <span className="leading-relaxed">Point it at the square — a link will pop up. Tap it.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#ff4500] text-white inline-flex items-center justify-center mono text-xs font-bold">3</span>
              <span className="leading-relaxed">On CampCompass, tap <strong>Save to my phone</strong> — done!</span>
            </li>
          </ol>
          <div className="flex items-center gap-2 mt-3 pt-3 border-t hairline text-[11px] mono text-[#888]">
            <Smartphone className="w-3.5 h-3.5" strokeWidth={1.6} />
            <span className="truncate">{prettyUrl}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
